import 'reflect-metadata';
import { STRIP_COMMENTS, ARGUMENT_NAMES, METADATA_TOKEN } from '../Constants';
function getParamNames(func) {
    const fnStr = func.toString().replace(STRIP_COMMENTS, '');
    return fnStr.slice(fnStr.indexOf('(') + 1, fnStr.indexOf(')')).match(ARGUMENT_NAMES) || [];
}
export function destroyMultitons(instances) {
    instances.forEach(instance => {
        if (instance && typeof instance === 'object' && instance.constructor) {
            if (instance.nodeType && instance.cloneNode) {
                return;
            }
            const options = Reflect.getMetadata(METADATA_TOKEN, instance.constructor);
            if (options && options.type === 'multiton' && options.dependencies) {
                destroyMultitons(Object.values(instance));
                typeof instance.onDestroy === 'function' && instance.onDestroy();
            }
        }
    });
}
export class Provider {
    static onError(error) { }
    static setOnError(onError) {
        Provider.onError = onError;
    }
    constructor(providers, token, autodestroy) {
        this.providers = providers;
        this.token = token;
        this.autodestroy = autodestroy;
        this.dependenciesMap = new Map();
        this.owners = 0;
    }
    forceUseSyncResolve() {
        this.canSyncResolve = true;
        return this.canSyncResolve;
    }
    canUseSyncResolve(keys) {
        if (typeof this.canSyncResolve === 'boolean') {
            return this.canSyncResolve;
        }
        if (keys) {
            if (keys.length <= 0) {
                return true;
            }
            const registrations = this.getRegistrations();
            for (let i = 0; i < registrations.length; i++) {
                const registration = registrations[i];
                if (!keys.includes(registration.dependencyKey)) {
                    continue;
                }
                if (registration.proxy) {
                    if (registration.sync) {
                        continue;
                    }
                    return false;
                }
                if (registration.dependancyConstructor) {
                    if (registration.sync) {
                        continue;
                    }
                    if (!this.canUseSyncResolve(registration.params)) {
                        return false;
                    }
                    continue;
                }
                if (!registration.dependancyConstructor) {
                    return false;
                }
                if (!this.canUseSyncResolve(registration.params)) {
                    return false;
                }
            }
            return true;
        }
        return this.getLazyLoadedDependencies().length <= 0;
    }
    getParams(constructor, dependencies) {
        if (!dependencies) {
            return [];
        }
        if (Array.isArray(dependencies)) {
            return dependencies;
        }
        const paramNames = Provider.getParamNames(constructor);
        if (Provider.debug) {
            const invalidDependencies = Object.keys(dependencies).filter(key => !paramNames.includes(key));
            if (invalidDependencies.length > 0) {
                console.warn(`Constructor ${constructor.name} has invalid binded keys -> ${invalidDependencies.join(', ')}`);
            }
        }
        return paramNames.map(key => dependencies[key]);
    }
    addOwner() {
        this.owners += 1;
        return this.owners;
    }
    removeOwner() {
        this.owners -= 1;
        if (this.autodestroy && this.owners <= 0) {
            if (Array.from(this.dependenciesMap.values()).filter(dep => dep.provider === this.token).length > 0) {
                return this.owners;
            }
            this.destroy();
        }
        return this.owners;
    }
    getRegistration(dependencyKey) {
        return this.dependenciesMap.get(dependencyKey) || undefined;
    }
    register(type, dependencyKey, constructor, instance, params) {
        let registration = this.getRegistration(dependencyKey);
        if (registration) {
            registration.instance = instance;
        }
        else {
            registration = {
                provider: this.token,
                type,
                dependancyConstructor: constructor,
                instance,
                dependencyKey,
                params: params || [],
                to: []
            };
        }
        this.dependenciesMap.set(dependencyKey, registration);
        this.dependenciesMap.forEach(reg => {
            Object.entries(reg.instance || {}).forEach(([key, value]) => {
                if (value instanceof constructor) {
                    reg.instance[key] = instance;
                    this.dependenciesMap.set(reg.dependencyKey, reg);
                }
            });
        });
        return registration;
    }
    resolveSync(dependencyKey) {
        if (dependencyKey === undefined) {
            return undefined;
        }
        const registration = this.getRegistration(dependencyKey);
        if (!registration) {
            console.warn(`Dependency is not registered in ${this.token.toString()}! ${dependencyKey.toString()}`);
            return undefined;
        }
        if (registration.proxy) {
            const provider = this.providers.loadProvider(registration.proxy.provider);
            if (!provider) {
                console.warn(`There is not provider ${registration.proxy.provider.toString()}`);
                return undefined;
            }
            provider.addProviderToDependency(registration.proxy.type, this.token);
            return provider.resolveSync(registration.proxy.type);
        }
        if (registration.type === 'multiton' && registration.dependancyConstructor) {
            const deps = this.executeQueueSync(registration.params);
            return new registration.dependancyConstructor(...deps);
        }
        if (registration.type === 'singleton' && registration.dependancyConstructor) {
            if (registration.instance) {
                return registration.instance;
            }
            if (registration.factory) {
                return registration.factory();
            }
            const deps = this.executeQueueSync(registration.params);
            registration.instance = new registration.dependancyConstructor(...deps);
            this.dependenciesMap.set(registration.dependencyKey, registration);
            return registration.instance;
        }
        if (registration.import && !registration.dependancyConstructor) {
            console.warn(`Dependency "${dependencyKey.toString()}" cannot be created because it is lazy loaded but you try to create it sync.`);
        }
        return undefined;
    }
    resolve(dependencyKey) {
        return Promise.resolve()
            .then(() => {
            if (dependencyKey === undefined) {
                return undefined;
            }
            const registration = this.getRegistration(dependencyKey);
            if (!registration) {
                console.warn(`Dependency is not registered in ${this.token.toString()}! ${dependencyKey.toString()}`);
                return undefined;
            }
            if (registration.proxy) {
                const provider = this.providers.loadProvider(registration.proxy.provider);
                if (!provider) {
                    console.warn(`There is not provider ${registration.proxy.provider.toString()}`);
                    return undefined;
                }
                provider.addProviderToDependency(registration.proxy.type, this.token);
                return provider.resolve(registration.proxy.type);
            }
            if (registration.type === 'multiton' && registration.dependancyConstructor) {
                if (registration.factory) {
                    return registration.factory();
                }
                return this.executeQueue(registration.params)
                    .then(deps => new registration.dependancyConstructor(...deps));
            }
            if (registration.type === 'singleton' && registration.dependancyConstructor) {
                if (registration.instance) {
                    return registration.instance;
                }
                if (registration.factory) {
                    return registration.factory();
                }
                return this.executeQueue(registration.params)
                    .then(deps => {
                    registration.instance = new registration.dependancyConstructor(...deps);
                    this.dependenciesMap.set(registration.dependencyKey, registration);
                    return registration.instance;
                });
            }
            if (!registration.import) {
                return registration.instance;
            }
            if (registration.dependancyConstructor) {
                return this.executeQueue(registration.params)
                    .then(deps => new registration.dependancyConstructor(...deps));
            }
            return registration.import()
                .then((constructor) => {
                const metadata = Reflect.getMetadata(METADATA_TOKEN, constructor) || { type: 'multiton', dependencies: {} };
                registration.type = metadata.type;
                registration.dependancyConstructor = constructor;
                registration.params = this.getParams(constructor, metadata.dependencies);
                return this.executeQueue(registration.params)
                    .then(deps => {
                    const instance = new constructor(...deps);
                    if (registration.type === 'singleton') {
                        registration.instance = instance;
                        this.dependenciesMap.set(dependencyKey, registration);
                    }
                    else {
                        this.dependenciesMap.set(dependencyKey, registration);
                    }
                    return instance;
                });
            });
        });
    }
    addProviderToDependency(dependencyKey, provider) {
        const registration = this.getRegistration(dependencyKey);
        if (!registration) {
            return;
        }
        registration.to = [...registration.to, provider];
        this.replace(registration);
    }
    replace(registration) {
        this.dependenciesMap.set(registration.dependencyKey, registration);
        return registration;
    }
    executeQueueSync(deps) {
        return deps.map(dep => this.resolveSync(dep));
    }
    executeQueue(deps, results = []) {
        if (deps.length <= 0) {
            return Promise.resolve(results);
        }
        return this.resolve(deps[0])
            .then(value => {
            results.push(value);
            return this.executeQueue(deps.slice(1), results);
        })
            .catch(() => {
            results.push(undefined);
            return this.executeQueue(deps.slice(1), results);
        });
    }
    resolveManySync(deps = {}, existingDeps = {}) {
        const values = Object.entries(deps);
        const instances = this.executeQueueSync(values.map(([key, symbol]) => symbol));
        return instances.map((instance, i) => {
            var _a;
            const key = (_a = values[i]) === null || _a === void 0 ? void 0 : _a[0];
            return { key, value: existingDeps[key] || instance };
        });
    }
    resolveMany(deps = {}, existingDeps = {}) {
        const values = Object.entries(deps);
        return this.executeQueue(values.map(([key, symbol]) => symbol))
            .then(instances => instances.map((instance, i) => {
            var _a;
            const key = (_a = values[i]) === null || _a === void 0 ? void 0 : _a[0];
            return { key, value: existingDeps[key] || instance };
        }));
    }
    resolveByAction(action) {
        const registration = this.getRegistrations().find((registration) => registration.action === action);
        if (!registration) {
            return Promise.resolve(undefined);
        }
        if (this.canUseSyncResolve([registration.dependencyKey])) {
            return Promise.resolve(this.resolveSync(registration.dependencyKey));
        }
        return this.resolve(registration.dependencyKey);
    }
    resolveManyByAction(actions = {}) {
        const registrations = this.getRegistrations();
        const deps = Object.entries(actions).reduce((acc, [key, action]) => {
            const registration = registrations.find(registration => registration.action === action);
            if (registration) {
                acc[key] = registration.dependencyKey;
            }
            return acc;
        }, {});
        return this.resolveMany(deps)
            .then(values => {
            return values.reduce((acc, value) => {
                acc[value.key] = value.value;
                return acc;
            }, {});
        });
    }
    hasAction(action) {
        return !!this.getRegistrations().find(reg => reg.action === action);
    }
    replaceParamsFrom(provider, tokens) {
        tokens.forEach(token => {
            if (this.getRegistration(token)) {
                return;
            }
            const registration = provider.getRegistration(token);
            if (!registration) {
                return;
            }
            registration.to = [...registration.to, this.token];
            this.dependenciesMap.set(token, registration);
            provider.replace(registration);
            this.replaceParamsFrom(provider, registration.params);
        });
        return this;
    }
    bindMany(provider, dependencies) {
        dependencies.forEach(dep => {
            provider.bind(dep).withProxy({
                provider: this.token,
                type: dep
            });
        });
        return provider;
    }
    getRegistrations() {
        return Array.from(this.dependenciesMap.values());
    }
    bind(dependencyKey) {
        return {
            withProxy: (proxy) => {
                if (proxy.implementation) {
                    const registration = {
                        provider: this.token,
                        type: 'singleton',
                        dependancyConstructor: proxy.implementation,
                        dependencyKey,
                        instance: proxy.implementation,
                        params: [],
                        to: []
                    };
                    this.dependenciesMap.set(dependencyKey, registration);
                    return registration;
                }
                const registration = {
                    provider: this.token,
                    proxy: proxy,
                    dependencyKey,
                    instance: undefined,
                    params: [],
                    to: []
                };
                this.dependenciesMap.set(dependencyKey, registration);
                return registration;
            },
            from: (provider) => {
                if (provider.token === this.token) {
                    console.warn(`You are trying to insert registration from the some provider ${this.token.toString()}`);
                    return null;
                }
                const registration = provider.getRegistration(dependencyKey);
                if (!registration) {
                    console.warn(`Dependency with key ${dependencyKey.toString()} is not found in provider ${provider.token.toString()}!`);
                    return null;
                }
                registration.to = [...registration.to, this.token];
                this.dependenciesMap.set(dependencyKey, registration);
                provider.replace(registration);
                this.replaceParamsFrom(provider, registration.params);
                return registration;
            },
            withFactory: (type, factory) => {
                if (this.dependenciesMap.has(dependencyKey)) {
                    return factory;
                }
                const registration = {
                    provider: this.token,
                    type,
                    dependancyConstructor: factory,
                    dependencyKey,
                    instance: undefined,
                    params: [],
                    factory,
                    to: []
                };
                this.dependenciesMap.set(dependencyKey, registration);
                return factory;
            },
            withValue: (value) => {
                if (this.dependenciesMap.has(dependencyKey)) {
                    return value;
                }
                const registration = {
                    provider: this.token,
                    type: 'singleton',
                    dependancyConstructor: value,
                    dependencyKey,
                    instance: value,
                    params: [],
                    to: []
                };
                this.dependenciesMap.set(dependencyKey, registration);
                return value;
            },
            with: (dependancyConstructor) => {
                const metadata = Reflect.getMetadata(METADATA_TOKEN, dependancyConstructor) || { type: 'multiton', dependencies: {} };
                const registration = {
                    provider: this.token,
                    type: metadata.type,
                    dependancyConstructor,
                    dependencyKey,
                    instance: undefined,
                    params: this.getParams(dependancyConstructor, metadata.dependencies),
                    to: []
                };
                this.dependenciesMap.set(dependencyKey, registration);
                return dependancyConstructor;
            },
            lazy: (factory) => {
                const registration = {
                    provider: this.token,
                    dependencyKey,
                    instance: undefined,
                    import: factory,
                    params: [],
                    to: []
                };
                this.dependenciesMap.set(dependencyKey, registration);
                return factory;
            },
            withAction: (action) => {
                const registration = this.dependenciesMap.get(dependencyKey);
                if (!registration) {
                    return null;
                }
                registration.action = action;
                this.dependenciesMap.set(dependencyKey, registration);
                return registration;
            }
        };
    }
    getLazyLoadedDependencies() {
        const allDeps = Array.from(this.dependenciesMap.values());
        const deps = allDeps.filter(register => register.import).filter(register => !register.dependancyConstructor);
        const proxies = allDeps
            .filter(x => x.proxy)
            .map(x => {
            const provider = this.providers.loadProvider(x.proxy.provider);
            if (!provider) {
                return x;
            }
            return provider.getRegistration(x.proxy.type);
        })
            .filter(register => register === null || register === void 0 ? void 0 : register.import)
            .filter(register => !(register === null || register === void 0 ? void 0 : register.dependancyConstructor));
        return [...deps, ...proxies];
    }
    loadDependencies(keys) {
        if (this.canSyncResolve) {
            return Promise.resolve([]);
        }
        let registrations = this.getRegistrations();
        if (keys) {
            if (keys.length <= 0) {
                return Promise.resolve([]);
            }
            registrations = registrations.filter(registration => keys.includes(registration.dependencyKey));
        }
        return this.importDependencies(registrations.map(reg => reg.dependencyKey)).then(() => ([]));
    }
    importDependencies(keys) {
        if (keys.length <= 0) {
            return Promise.resolve(true);
        }
        const registrations = this.getRegistrations().filter(registration => keys.includes(registration.dependencyKey));
        if (registrations.length <= 0) {
            return Promise.resolve(true);
        }
        return Promise.all(registrations.map(registration => this.importDedendency(registration)))
            .then(registrations => {
            const next_level_keys = registrations.reduce((acc, registration) => {
                var _a;
                (_a = registration.params) === null || _a === void 0 ? void 0 : _a.forEach(key => {
                    if (!keys.includes(key) && !acc.includes(key)) {
                        acc.push(key);
                    }
                });
                return acc;
            }, []);
            return this.importDependencies(next_level_keys);
        });
    }
    importDedendency(registration) {
        if (registration.proxy) {
            const provider = this.providers.loadProvider(registration.proxy.provider);
            if (!provider) {
                return Promise.resolve(registration);
            }
            return provider.loadDependencies([registration.proxy.type])
                .then(() => {
                const updatedRegistration = provider.getRegistration(registration.proxy.type);
                if (provider.canSyncResolve || (updatedRegistration === null || updatedRegistration === void 0 ? void 0 : updatedRegistration.sync) || ((updatedRegistration === null || updatedRegistration === void 0 ? void 0 : updatedRegistration.dependancyConstructor) && provider.canSyncResolve)) {
                    registration.sync = true;
                    this.dependenciesMap.set(registration.dependencyKey, registration);
                }
                return registration;
            })
                .catch(() => registration);
        }
        if (!registration.import || registration.dependancyConstructor) {
            return Promise.resolve(registration);
        }
        return registration.import()
            .then(dependancyConstructor => {
            const metadata = Reflect.getMetadata(METADATA_TOKEN, dependancyConstructor) || { type: 'multiton', dependencies: {} };
            registration.type = metadata.type;
            registration.dependancyConstructor = dependancyConstructor;
            registration.params = this.getParams(dependancyConstructor, metadata.dependencies);
            registration.sync = true;
            this.dependenciesMap.set(registration.dependencyKey, registration);
            return registration;
        })
            .catch((error) => {
            console.warn(error);
            Provider.onError(error);
            return registration;
        });
    }
    destroyInstance(instance) {
        if (!instance) {
            return;
        }
        instance.constructor && Provider.destroyMultitons(Object.values(instance));
        return typeof instance.onDestroy === 'function' && instance.onDestroy();
    }
    onDestroy() { }
    setOnDestroy(onDestroy) {
        this.onDestroy = onDestroy;
        return this;
    }
    destroyProxyDependency(dependencyKey, provider) {
        const registration = this.getRegistration(dependencyKey);
        if (!registration) {
            return;
        }
        registration.to = registration.to.filter(x => x !== provider);
        registration.to.forEach(token => {
            const bindedProvider = this.providers.getProvider(token);
            bindedProvider === null || bindedProvider === void 0 ? void 0 : bindedProvider.replace(registration);
        });
        if (registration.to.length <= 0) {
            const isUsed = this.getRegistrations().filter(x => x.params.includes(dependencyKey) && x.instance).length > 0;
            if (!isUsed) {
                this.destroyInstance(registration.instance);
                registration.instance = undefined;
            }
        }
        this.replace(registration);
    }
    destroy() {
        this.dependenciesMap.forEach(registration => {
            if (registration.instance && typeof registration.instance === 'object') {
                if (registration.provider === this.token) {
                    registration.to.length <= 0 && this.destroyInstance(registration.instance);
                }
                else {
                    const provider = this.providers.getProvider(registration.provider);
                    provider === null || provider === void 0 ? void 0 : provider.destroyProxyDependency(registration.dependencyKey, this.token);
                }
            }
            else if (registration.proxy) {
                const provider = this.providers.getProvider(registration.proxy.provider);
                provider === null || provider === void 0 ? void 0 : provider.destroyProxyDependency(registration.proxy.type, this.token);
            }
        });
        this.providers.deleteProvider(this.token);
        this.onDestroy();
    }
}
Provider.debug = false;
Provider.getParamNames = getParamNames;
Provider.destroyMultitons = destroyMultitons;
